import { routesDefinitions } from "lib/actions/routeDefinitions";
import { useCallback, useEffect } from "react";
import { useRouter } from "next/router";
import { useCesium } from "../../useCesium";
import { useCurrentProjectId } from "lib/hooks/useProject";
import { useAssets } from "lib/hooks/assets/useAssets";
import { UrlObject } from "url";
import { Cesium3DTileset } from "cesium";
import { AssetVersionWithAssetModel } from "lib/types/assets/AssetVersionWithAssetModel";
import { getAssetPageType } from "lib/actions/assets/getAssetPageType";
import TilesetPlugin from "../../wrapper/TilesetPlugin";

export function useToolEffectSelectionNavigation() {
  const router = useRouter();
  const { selectionToolPlugin, selectedElementPlugin } = useCesium();
  const projectId = useCurrentProjectId();
  const { versions } = useAssets(projectId);

  const openFeaturePanel = useCallback(
    (
      projectId: number,
      assetId: number,
      versionId: number,
      elementId: string,
      path: string,
      assetPageType: string,
    ) => {
      router.push({
        pathname: path,
        query: {
          projectId,
          assetPageType,
          assetId,
          versionId,
          elementId,
        },
      } as UrlObject);
    },
    [router.pathname],
  );

  const openTilesetPanel = useCallback(
    (
      projectId: number,
      assetId: number,
      versionId: number,
      assetPageType: string,
    ) => {
      router.push({
        pathname:
          "/projects/[projectId]/[assetPageType]/[assetId]/versions/[versionId]/selected",
        query: {
          projectId,
          assetPageType,
          assetId,
          versionId,
        },
      } as UrlObject);
    },
    [router.pathname],
  );

  const closePanel = useCallback(() => {
    if (
      (routesDefinitions.modelElement.regex.test(router.pathname) &&
        selectedElementPlugin.isFeatureSelected.getSnapshot()) ||
      routesDefinitions.selectedAsset.regex.test(router.pathname)
    ) {
      router.push({
        pathname: "/projects/[projectId]/[assetPageType]",
        query: {
          projectId: router.query.projectId,
          assetPageType: router.query.assetPageType,
        },
      } as UrlObject);
    }
  }, [router.pathname, router.query]);

  useEffect(() => {
    return selectionToolPlugin.featureClickedEvent.addHandler((object) => {
      if (TilesetPlugin.isFeature(object)) {
        const version =
          versions && getAssetVersionFromTileset(object.tileset, versions);
        if (!version) return;

        const elementId = TilesetPlugin.getElementIdFromFeature(object);
        if (!elementId) return;

        const versionId = version.id,
          assetId = version.asset.id,
          projectId = version.asset.projectId;
        const assetPageType = getAssetPageType(
          version.asset.type,
          version.asset.extension,
        );
        const path = routesDefinitions.modelElementProperties.regex.test(
          router.pathname,
        )
          ? routesDefinitions.modelElementProperties.href.pathname
          : routesDefinitions.modelElement.href.pathname;
        openFeaturePanel(
          projectId,
          assetId,
          versionId,
          elementId,
          path,
          assetPageType,
        );
      } else if (object instanceof Cesium3DTileset) {
        const version =
          versions && getAssetVersionFromTileset(object, versions);
        if (!version) return;

        const versionId = version.id,
          assetId = version.asset.id,
          projectId = version.asset.projectId;
        const assetPageType = getAssetPageType(
          version.asset.type,
          version.asset.extension,
        );
        openTilesetPanel(projectId, assetId, versionId, assetPageType);
      }
    });
  }, [router.pathname, versions, openFeaturePanel, openTilesetPanel]);

  useEffect(() => {
    return selectionToolPlugin.emptyClickedEvent.addHandler(() => {
      closePanel();
    });
  }, [closePanel]);

  useEffect(() => {
    return selectionToolPlugin.deactivatedEvent.addHandler(() => {
      if (selectedElementPlugin.isFeatureSelected.getSnapshot()) closePanel();
    });
  }, [closePanel]);
}

function getAssetVersionFromTileset(
  tileset: Cesium3DTileset,
  versions: AssetVersionWithAssetModel[],
) {
  const tilesetId = TilesetPlugin.getTilesetIdFromTileset(tileset);
  const asset =
    versions.find((version) => version.contentUri === tilesetId) ?? null;
  return asset;
}
